import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Send20Regular, Attach20Regular, Dismiss12Regular, Emoji20Regular } from "@fluentui/react-icons";
import { chatService, type ChatAttachment } from "../../services/chatService";
import { useResponsive } from "../../hooks/useResponsive";
import { InitialsAvatar } from "./chatUi";
import { AUB, INK, INK2, LINE, BG_SOFT, PLUM } from "./chatTheme";
import { COMPOSER_MAX_HOEHE, composerHoehe } from "./composerHoehe";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

const EMOJIS = ["👍", "❤️", "😂", "🎉", "🙏", "👀", "😊", "🔥", "✅", "🙌", "🤝", "💡"];

/** Ein im aktuellen Chat erwähnbares Mitglied (für das @-Autocomplete). */
export type MentionMember = {
  userId: string;
  /** Anzeigename („Nachname, Vorname"). */
  name: string;
  /** Kürzel/Username (space-frei, wird als Token verwendet); kann fehlen. */
  kuerzel?: string | null;
};

type Props = {
  /** Fehlt im Entwurf eines neuen Chats — dann gibt es (noch) keine Anhänge. */
  conversationId?: string;
  onSubmit: (input: {
    text: string;
    attachments: ChatAttachment[];
    replyToId?: string;
    mentionedUserIds: string[];
  }) => Promise<unknown> | void;
  onTyping?: () => void;
  replyTo?: { id: string; authorName: string | null; body: string } | null;
  onCancelReply?: () => void;
  /** Erwähnbare Mitglieder der aktuellen Conversation (für @-Autocomplete). */
  members?: MentionMember[];
  placeholder?: string;
  submitLabel?: string;
  autoFocus?: boolean;
};

/** Erkennt einen laufenden „@token" unmittelbar vor dem Cursor. */
const activeMention = (text: string, caret: number): { start: number; query: string } | null => {
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf("@");
  if (at < 0) return null;
  // „@" muss am Wortanfang stehen (Zeilenanfang oder Whitespace davor).
  if (at > 0 && !/\s/.test(upto[at - 1])) return null;
  const between = upto.slice(at + 1);
  if (/\s/.test(between)) return null; // Whitespace beendet das Token
  return { start: at, query: between };
};

/** Token, das für eine Erwähnung in den Text geschrieben wird (Kürzel bevorzugt). */
const mentionToken = (m: MentionMember): string => m.kuerzel || m.name;

/**
 * Chat-Eingabe: Text, @-Erwähnungen (Autocomplete der Conversation-Mitglieder),
 * Datei-Anhänge (Klick + Drag-Drop), Emoji-Auswahl, Antwort-Vorschau, Enter zum
 * Senden (Shift+Enter = Zeilenumbruch).
 */
export const ChatComposer: React.FC<Props> = ({
  conversationId,
  onSubmit,
  onTyping,
  replyTo,
  onCancelReply,
  members,
  placeholder,
  submitLabel = "Senden",
  autoFocus,
}) => {
  const { isPhone } = useResponsive();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  // @-Erwähnungen
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [caret, setCaret] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [mentionSuppressed, setMentionSuppressed] = useState(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const emojiRef = useRef<HTMLDivElement | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || !files.length || !conversationId) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        try {
          const a = await chatService.uploadAttachment(conversationId, f);
          setAttachments((prev) => [...prev, a]);
        } catch (e) {
          console.error("Upload fehlgeschlagen", e);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (autoFocus && taRef.current) {
      taRef.current.focus();
      const end = taRef.current.value.length;
      taRef.current.setSelectionRange(end, end);
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!emojiOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) setEmojiOpen(false);
    };
    alleDokumente.addEventListener("pointerdown", onDoc);
    return () => alleDokumente.removeEventListener("pointerdown", onDoc);
  }, [emojiOpen]);

  // T-349: Die Eingabe wächst mit dem Text mit (bis COMPOSER_MAX_HOEHE, dann
  // scrollt sie intern). Erst auf `auto` zurücksetzen, damit sie nach dem
  // Löschen bzw. Senden (`setText("")`) auch wieder schrumpft. LayoutEffect,
  // damit beim Tippen nichts flackert.
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const { height, overflowY } = composerHoehe(el.scrollHeight, {
      min: isPhone ? 44 : 40,
      max: COMPOSER_MAX_HOEHE,
      rahmen: 2,
    });
    el.style.height = `${height}px`;
    el.style.overflowY = overflowY;
  }, [text, isPhone]);

  // --- @-Erwähnungen: laufendes Token + Vorschlagsliste der Mitglieder ---
  const mention = activeMention(text, caret);
  const suggestions = useMemo<MentionMember[]>(() => {
    if (!mention || !members || members.length === 0) return [];
    const q = mention.query.trim().toLowerCase();
    return members
      .filter(
        (m) =>
          !q ||
          m.name.toLowerCase().includes(q) ||
          (m.kuerzel ?? "").toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [mention, members]);
  const mentionOpen = !!mention && !mentionSuppressed && suggestions.length > 0;

  // Highlight zurücksetzen, sobald sich die Suchanfrage ändert.
  useEffect(() => {
    setHighlight(0);
  }, [mention?.query]);

  const pickMention = (m: MentionMember) => {
    if (!mention) return;
    const before = text.slice(0, mention.start);
    const after = text.slice(caret);
    const inserted = `@${mentionToken(m)} `;
    const next = before + inserted + after;
    setText(next);
    setMentionIds((ids) => (ids.includes(m.userId) ? ids : [...ids, m.userId]));
    setMentionSuppressed(false);
    const newCaret = (before + inserted).length;
    requestAnimationFrame(() => {
      if (taRef.current) {
        taRef.current.focus();
        taRef.current.setSelectionRange(newCaret, newCaret);
        setCaret(newCaret);
      }
    });
  };

  const insertEmoji = (emoji: string) => {
    setText((t) => t + emoji);
    setEmojiOpen(false);
    requestAnimationFrame(() => taRef.current?.focus());
  };

  const submit = async () => {
    const body = text.trim();
    if (!body && attachments.length === 0) return;
    setBusy(true);
    try {
      // Nur Erwähnungen behalten, deren @Token noch im Text steht.
      const mentionedUserIds = mentionIds.filter((id) => {
        const m = members?.find((x) => x.userId === id);
        return m && body.includes(`@${mentionToken(m)}`);
      });
      await onSubmit({ text: body, attachments, replyToId: replyTo?.id, mentionedUserIds });
      setText("");
      setAttachments([]);
      setMentionIds([]);
      onCancelReply?.();
    } catch (e) {
      console.error("Nachricht senden fehlgeschlagen", e);
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = !busy && (text.trim().length > 0 || attachments.length > 0);

  const syncCaret = (el: HTMLTextAreaElement) => setCaret(el.selectionStart ?? el.value.length);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pickMention(suggestions[Math.min(highlight, suggestions.length - 1)]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionSuppressed(true);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  const roundBtn = (extra?: React.CSSProperties): React.CSSProperties => ({
    flexShrink: 0,
    width: isPhone ? 44 : 40,
    height: isPhone ? 44 : 40,
    borderRadius: "50%",
    border: `1px solid ${LINE}`,
    background: "#fff",
    color: AUB,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...extra,
  });

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        void handleFiles(e.dataTransfer.files);
      }}
      style={{ display: "flex", flexDirection: "column", gap: 6 }}
    >
      {replyTo && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, borderLeft: `3px solid ${AUB}`, background: BG_SOFT, borderRadius: 6, padding: "4px 8px", fontSize: 12 }}>
          <span style={{ color: INK2, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Antwort an <strong style={{ color: INK }}>{replyTo.authorName ?? "Unbekannt"}</strong>
            {replyTo.body ? `: ${replyTo.body}` : ""}
          </span>
          <button type="button" aria-label="Antwort verwerfen" onClick={onCancelReply} style={{ border: "none", background: "transparent", cursor: "pointer", color: INK2, display: "inline-flex" }}>
            <Dismiss12Regular />
          </button>
        </div>
      )}
      {(attachments.length > 0 || uploading) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          {attachments.map((a) => (
            <span key={a.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: 240, border: `1px solid ${LINE}`, background: "#faf6fb", borderRadius: 999, padding: "3px 6px 3px 10px", fontSize: 12, color: INK }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
              <button type="button" aria-label="Anhang entfernen" onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))} style={{ border: "none", background: "transparent", cursor: "pointer", color: INK2, display: "inline-flex", padding: 0 }}>
                <Dismiss12Regular />
              </button>
            </span>
          ))}
          {uploading && <span style={{ fontSize: 12, color: INK2 }}>lädt hoch…</span>}
        </div>
      )}
      {/* Eingabe und die runden Buttons liegen auf EINER Mittelachse: die Textarea
          ist bei einer Zeile exakt so hoch wie ein Button (Höhe = 2×padding +
          lineHeight + Rahmen), `display:block` nimmt ihr den inline-block-
          Descender (~4px), der den Wrapper sonst höher machte als das Feld
          selbst — daher wirkte die Eingabe gegenüber den Buttons nach oben
          versetzt. Mehrzeilig wächst die Textarea über `composerHoehe` bis
          `COMPOSER_MAX_HOEHE` mit (T-349); `flex-end` hält die Buttons dabei
          unten. */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
        {/* Verstecktes Datei-Input (kein Layout-Einfluss); der sichtbare Anhängen-Button
            sitzt rechts bei Emoji/Senden, damit die Textarea linksbündig mit der
            Thread-Kante (16px) fluchtet statt hinter dem Button einzurücken. */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = "";
          }}
        />

        <div style={{ position: "relative", flex: 1 }}>
          {mentionOpen && (
            <div
              role="listbox"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: "100%",
                marginBottom: 6,
                zIndex: 40,
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: 12,
                boxShadow: "0 -10px 28px rgba(20,8,20,0.14)",
                padding: 4,
                maxHeight: 248,
                overflowY: "auto",
              }}
            >
              {suggestions.map((m, i) => (
                <div
                  key={m.userId}
                  role="option"
                  aria-selected={i === highlight}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickMention(m);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "6px 8px",
                    borderRadius: 8,
                    cursor: "pointer",
                    background: i === highlight ? "#faf6fb" : "transparent",
                  }}
                >
                  <InitialsAvatar name={m.name} userId={m.userId} size={28} />
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, color: INK }}>
                    {m.name}
                  </span>
                  {m.kuerzel && (
                    <span style={{ fontSize: 11, color: INK2, flexShrink: 0 }}>@{m.kuerzel}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          <textarea
            ref={taRef}
            value={text}
            placeholder={placeholder ?? "Nachricht schreiben…"}
            disabled={busy}
            rows={1}
            onChange={(e) => {
              setText(e.target.value);
              syncCaret(e.target);
              setMentionSuppressed(false);
              onTyping?.();
            }}
            onKeyDown={onKeyDown}
            onKeyUp={(e) => syncCaret(e.currentTarget)}
            onClick={(e) => syncCaret(e.currentTarget)}
            onSelect={(e) => syncCaret(e.currentTarget)}
            style={{
              display: "block",
              width: "100%",
              boxSizing: "border-box",
              minHeight: isPhone ? 44 : 40,
              maxHeight: COMPOSER_MAX_HOEHE,
              // 2×padding + lineHeight + 2×Rahmen == Buttonhöhe (44 bzw. 40) →
              // die eine Zeile sitzt exakt auf der Achse der Button-Icons.
              padding: isPhone ? "12px 14px" : "10px 14px",
              lineHeight: "18px",
              border: `1px solid ${LINE}`,
              borderRadius: 20,
              fontFamily: "inherit",
              fontSize: isPhone ? 16 : 13.5,
              resize: "none",
              backgroundColor: BG_SOFT,
            }}
          />
        </div>

        <button type="button" aria-label="Datei anhängen" title="Datei anhängen" onClick={() => fileInputRef.current?.click()} disabled={busy} style={{ ...roundBtn(), display: conversationId ? undefined : "none" }}>
          <Attach20Regular />
        </button>

        <div style={{ position: "relative" }} ref={emojiRef}>
          <button type="button" aria-label="Emoji einfügen" title="Emoji" onClick={() => setEmojiOpen((o) => !o)} disabled={busy} style={roundBtn(emojiOpen ? { background: "#faf6fb" } : undefined)}>
            <Emoji20Regular />
          </button>
          {emojiOpen && (
            <div
              style={{
                position: "absolute",
                bottom: 48,
                right: 0,
                zIndex: 40,
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: 12,
                boxShadow: "0 12px 32px rgba(20,8,20,0.16)",
                padding: 8,
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: 2,
                width: 232,
              }}
            >
              {EMOJIS.map((e) => (
                <button key={e} type="button" onClick={() => insertEmoji(e)} style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer", padding: 4, borderRadius: 8 }}>
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          aria-label={submitLabel}
          title={submitLabel}
          style={{
            flexShrink: 0,
            width: isPhone ? 44 : 40,
            height: isPhone ? 44 : 40,
            borderRadius: "50%",
            border: "none",
            background: PLUM,
            color: "#fff",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: canSubmit ? 1 : 0.5,
          }}
        >
          <Send20Regular />
        </button>
      </div>
    </div>
  );
};

export default ChatComposer;
